import React, { useContext, useEffect, useMemo } from "react";
import { useRouteError, Link } from "react-router-dom";
import { ThemeContext, ThemeContextData } from "../../contexts/ThemeContext";
import SEOHead from "../../components/seo/SEOHead";

const isChunkLoadError = (error) => {
  if (!error) return false;
  const message = error.message || error.toString() || '';
  return (
    error.name === 'ChunkLoadError' ||
    /Loading chunk [\d]+ failed/i.test(message) ||
    /Loading CSS chunk [\d]+ failed/i.test(message) ||
    /Failed to fetch dynamically imported module/i.test(message)
  );
};

const RELOAD_FLAG = 'errorPageChunkReload';

const ErrorPage = () => {
  const themeContext = useContext(ThemeContext);
  const theme = ThemeContextData[themeContext];
  const error = useRouteError();

  const chunkError = useMemo(() => isChunkLoadError(error), [error]);

  // If this is a chunk-load failure caused by a stale deploy, reload once
  // so the browser pulls the latest index.html + chunk manifest. Guarded
  // by sessionStorage to avoid infinite loops if reload itself fails.
  useEffect(() => {
    if (!chunkError) return;
    let alreadyReloaded = false;
    try {
      alreadyReloaded = window.sessionStorage.getItem(RELOAD_FLAG) === '1';
    } catch (_) {
      // ignore
    }
    if (alreadyReloaded) return;
    try {
      window.sessionStorage.setItem(RELOAD_FLAG, '1');
    } catch (_) {
      // ignore
    }
    window.location.reload();
  }, [chunkError]);

  // Clear the reload flag on a successful (non-chunk) render of ErrorPage.
  useEffect(() => {
    if (chunkError) return;
    try {
      window.sessionStorage.removeItem(RELOAD_FLAG);
    } catch (_) {
      // ignore
    }
  }, [chunkError]);

  if (chunkError) {
    // While the auto-reload is in flight, render a minimal placeholder so
    // the user doesn't see the "Unexpected Application Error!" default
    // text from React Router or our own error markup.
    return (
      <section className={`${theme.textNormal} ${theme.background} body-font`}>
        <SEOHead title="Updating..." description="Loading the latest version of Grip It." noIndex />
        <div className="container px-5 py-24 mx-auto flex flex-col items-center text-center">
          <h2 className="text-3xl font-medium mb-4">Updating to the latest version…</h2>
          <p className="leading-relaxed">One moment please.</p>
        </div>
      </section>
    );
  }

  return (
    <section className={`${theme.textNormal} ${theme.background} body-font`}>
      <SEOHead
        title="Page Not Found"
        description="The page you're looking for doesn't exist. Explore Grip It's online calisthenics programs, blogs, and classes instead."
        canonical="/"
        noIndex
      />
      <div className="container px-5 py-24 mx-auto flex flex-col items-center text-center">
        <div className={`lg:w-2/6 md:w-1/2 rounded-lg p-8 flex flex-col mt-10 ${theme.backgroundSecondary}`}>
          <h2 className="text-3xl font-medium title-font mb-4">Oops!</h2>
          <p className="leading-relaxed mb-6">Sorry, the page you're looking for doesn't exist or an unexpected error has occurred.</p>
          {error && (error.statusText || error.message) && (
            <p className={`italic mb-6 ${theme.textFocus}`}>{error.statusText || error.message}</p>
          )}
          <Link
            to="/"
            className="inline-block mt-2 px-6 py-2 rounded-full bg-brand-500 text-white hover:bg-brand-600 transition-colors"
          >
            Back to Home
          </Link>
        </div>
      </div>
    </section>
  );
};

export default ErrorPage;
